import { useMemo } from "react";
import { UserRole } from "avvir";

import useProject from "../../../queries/projects/use_project";
import useWbsAvailability from "../../../queries/projects/use_wbs_availability";
import { PageOption } from "../nav_bar_pages_dropdown";
import { destinationItems, iconItems, overflowItems, RedesignNavItem } from "./nav_bar_redesign_options";
import { useFlags } from "../../../services/utilities/launch_darkly_helper";
import { usePermissionService } from "../../../queries/user/use_permission_service";

export type RedesignNavGroups = {
  destinations: RedesignNavItem[]
  icons: RedesignNavItem[]
  overflow: RedesignNavItem[]
};

// Filters the reskinned nav groups using the SAME data + flags + `visible` predicates the legacy
// dropdown uses (see nav_bar_pages_dropdown.tsx), so role/permission gating is identical.
const useRedesignNavGroups = (projectId: string, userRole: UserRole): RedesignNavGroups => {
  const { schedulePages, customerFacingManageUsers, dataDashboard } = useFlags();
  const { data: project } = useProject(projectId);
  const { data: wbsIsAvailable } = useWbsAvailability(projectId);
  const permissionService = usePermissionService();

  return useMemo(() => {
    const isVisible = (base: PageOption) => base.visible({
      userRole,
      wbsIsAvailable: project?.settings?.progress5d || wbsIsAvailable,
      project,
      permissionService,
    }, {
      schedulePages,
      customerFacingManageUsers,
      dataDashboard,
    });

    return {
      destinations: destinationItems.filter((item) => isVisible(item.base)),
      icons: iconItems.filter((item) => isVisible(item.base)),
      overflow: overflowItems.filter((item) => isVisible(item.base)),
    };
  }, [project, wbsIsAvailable, permissionService, userRole, schedulePages, customerFacingManageUsers, dataDashboard]);
};

export default useRedesignNavGroups;
